import { ReactNode } from 'react'

const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
  const angleInRadians = ((angleInDegrees - 180) * Math.PI) / 180.0
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  }
}

const calculateArc = (
  x: number,
  y: number,
  radius: number,
  startAngle: number,
  endAngle: number,
  label: string,
  color: string
) => {
  const start = polarToCartesian(x, y, radius, endAngle)
  const end = polarToCartesian(x, y, radius, startAngle)
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'
  return (
    <path
      key={label}
      aria-label={label}
      stroke={color}
      fill="none"
      strokeWidth="3.3"
      strokeLinecap="round"
      d={`M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`}
    />
  )
}

const createMask = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
  const start = polarToCartesian(x, y, radius, endAngle)
  const end = polarToCartesian(x, y, radius, startAngle)
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'
  return (
    <defs key="defs">
      <mask id="m0" width="100" height="100" x="-10" y="-10">
        <path
          stroke="#FFFFFF"
          fill="none"
          strokeWidth="3.3"
          strokeLinecap="round"
          d={`M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`}
        />
      </mask>
    </defs>
  )
}

const GAP = 0.75
const calculateArcWithSegments = (
  x: number,
  y: number,
  radius: number,
  startAngle: number,
  endAngle: number,
  segments: Segment[] = []
) => {
  const totalAngle = endAngle - startAngle
  let runningPercentage = 0
  return (
    <g mask="url(#m0)" key="segments">
      {segments.map(({ color, label, percentage }) => {
        const start = polarToCartesian(
          x,
          y,
          radius,
          startAngle + (runningPercentage + percentage) * totalAngle - GAP / 2
        )
        const end = polarToCartesian(x, y, radius, startAngle + runningPercentage * totalAngle + GAP / 2)
        const angle = (runningPercentage + percentage) * totalAngle - GAP - runningPercentage * totalAngle
        const largeArcFlag = angle <= 180 ? '0' : '1'
        runningPercentage += percentage
        return (
          <path
            key={label}
            aria-label={label}
            stroke={color}
            fill="none"
            strokeWidth="3.3"
            strokeLinecap="butt"
            d={`M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`}
          />
        )
      })}
    </g>
  )
}

export type Segment = { label: string; color: string; percentage: number }
export type SliceBasics = { id: string; label: string; percentage: number }
export type SliceWithColor = SliceBasics & { color: string; segments?: never }
export type SliceWithSegments = SliceBasics & { color?: never; segments: Segment[] }
export type Slice = SliceWithColor | SliceWithSegments
export const createArcs = (data: Slice[]) => {
  let currentAngle = 90
  const nonZeroPercentages = data.filter(({ percentage }) => percentage > 0)
  const largestPercentageId = [...nonZeroPercentages].sort((a, b) => b.percentage - a.percentage)[0].id

  let offset = 0 // if we have percentage values less than 2, we need to take away from the largest value
  const MINIMUM_VALUE = 2 // percentage values less than 2 are too small to be visible, minimum value is 2 to just create a dot
  const BUFFER = 6 // empty space between arcs
  const arcs = nonZeroPercentages.map(({ id, label, color, percentage, segments }) => {
    if (percentage < MINIMUM_VALUE) {
      offset += MINIMUM_VALUE - percentage + BUFFER / 2
      percentage = MINIMUM_VALUE
    }
    const startAngle = currentAngle + BUFFER / 2
    let endAngle = currentAngle + (percentage / 100) * 360
    currentAngle = endAngle
    endAngle = endAngle - BUFFER / 2
    return { id, label, color, startAngle, endAngle, segments: segments?.filter(({ percentage }) => percentage > 0) }
  })

  let hasLargestPercentageBeenSeen = false
  const arcsSvg: ReactNode[] = []
  const masks: ReactNode[] = []
  arcs.forEach(({ id, label, color, startAngle, endAngle, segments }) => {
    const startOffset = hasLargestPercentageBeenSeen ? offset : 0
    if (id === largestPercentageId) hasLargestPercentageBeenSeen = true
    const endOffset = hasLargestPercentageBeenSeen ? offset : 0
    if (segments || !color) {
      masks.push(createMask(55, 55, 50, startAngle - startOffset, endAngle - endOffset))
      arcsSvg.push(
        calculateArcWithSegments(55, 55, 50, startAngle - startOffset - 2.25, endAngle - endOffset + 2.25, segments)
      )
    } else {
      arcsSvg.push(calculateArc(55, 55, 50, startAngle - startOffset, endAngle - endOffset, label, color))
    }
  })
  return { arcs: arcsSvg, masks }
}
